/**
 * Share Scan Modal Component
 *
 * Modal for creating and managing shareable links for LLM scan reports.
 */

import { useState } from "react";
import { Copy, Link2, Trash2, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateShare, useScanShares, useRevokeShare } from "@/hooks/useShareScan";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface ShareScanModalProps {
  scanUuid: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ShareScanModal({ scanUuid, open, onOpenChange }: ShareScanModalProps) {
  const [expiresInDays, setExpiresInDays] = useState<number | null>(7);
  const [passwordEnabled, setPasswordEnabled] = useState(false);
  const [password, setPassword] = useState("");
  const [copiedShareUrl, setCopiedShareUrl] = useState<string | null>(null);
  const [revokingShareUuid, setRevokingShareUuid] = useState<string | null>(null);
  const [justCreatedUrl, setJustCreatedUrl] = useState<string | null>(null);

  const { data: shares, isLoading: sharesLoading, refetch } = useScanShares(scanUuid);
  const createShareMutation = useCreateShare(scanUuid);
  const revokeShareMutation = useRevokeShare(scanUuid);

  // Reset state when modal closes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setJustCreatedUrl(null);
      setCopiedShareUrl(null);
    }
    onOpenChange(open);
  };

  const handleCreateShare = async () => {
    try {
      const result = await createShareMutation.mutateAsync({
        expires_in_days: expiresInDays,
        password: passwordEnabled && password ? password : null,
      });

      if (result.share_url) {
        setJustCreatedUrl(result.share_url);
        await navigator.clipboard.writeText(result.share_url);
        setCopiedShareUrl(result.share_url);
        setTimeout(() => setCopiedShareUrl(null), 3000);
      }

      await refetch();

      setPasswordEnabled(false);
      setPassword("");
      setExpiresInDays(7);
    } catch (error) {
      console.error("Failed to create share:", error);
    }
  };

  const handleRevokeShare = async (shareUuid: string) => {
    if (revokingShareUuid) return;
    setRevokingShareUuid(shareUuid);

    if (confirm("Are you sure you want to revoke this share link?")) {
      try {
        await revokeShareMutation.mutateAsync(shareUuid);
        // Clear success message so user can create new share
        setJustCreatedUrl(null);
      } catch (error) {
        console.error("Failed to revoke share:", error);
      } finally {
        setRevokingShareUuid(null);
      }
    } else {
      setRevokingShareUuid(null);
    }
  };

  const handleCopyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedShareUrl(url);
      setTimeout(() => setCopiedShareUrl(null), 2000);
    } catch (error) {
      console.error("Failed to copy link:", error);
    }
  };

  const formatExpiryDate = (expiresAt: string | null) => {
    if (!expiresAt) return "Never";
    const date = new Date(expiresAt);
    return date.toLocaleDateString();
  };

  const isExpired = (expiresAt: string | null) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const activeShares =
    shares?.filter((share) => !share.is_revoked && !isExpired(share.expires_at)) || [];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Share Scan Report
          </DialogTitle>
          <DialogDescription>
            Create a shareable link to allow others to view this scan report. You can set an
            expiration date and optional password protection.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Create New Share Section */}
          <div className="space-y-4 border rounded-lg p-4">
            <h3 className="text-sm font-semibold">Create New Share Link</h3>

            {/* Success message after creating */}
            {justCreatedUrl && (
              <div className="p-3 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                  <CheckCircle2 className="h-4 w-4" />
                  <span className="text-sm font-medium">Share link created and copied to clipboard!</span>
                </div>
                <code className="mt-2 block text-xs bg-green-100 dark:bg-green-900 px-2 py-1 rounded truncate">
                  {justCreatedUrl}
                </code>
              </div>
            )}

            {activeShares.length > 0 ? (
              <p className="text-sm text-muted-foreground">
                You already have an active share link for this scan. Revoke it below to create a new
                one with different settings.
              </p>
            ) : (
              <>
                {/* Expiry Selection */}
                <div className="space-y-2">
                  <Label htmlFor="expiry">Expires In</Label>
                  <Select
                    value={expiresInDays?.toString() || "never"}
                    onValueChange={(value) =>
                      setExpiresInDays(value === "never" ? null : parseInt(value))
                    }
                  >
                    <SelectTrigger id="expiry">
                      <SelectValue placeholder="Select expiry" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 day</SelectItem>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="never">Never</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Password Protection */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="password-enabled"
                      checked={passwordEnabled}
                      onCheckedChange={(checked) => {
                        setPasswordEnabled(checked === true);
                        if (!checked) setPassword("");
                      }}
                    />
                    <Label htmlFor="password-enabled" className="cursor-pointer">
                      Password protect this link
                    </Label>
                  </div>
                  {passwordEnabled && (
                    <Input
                      type="password"
                      placeholder="Enter password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      minLength={4}
                    />
                  )}
                </div>

                {/* Create Button */}
                <Button
                  onClick={handleCreateShare}
                  disabled={
                    createShareMutation.isPending || (passwordEnabled && password.length < 4)
                  }
                  className="w-full"
                >
                  {createShareMutation.isPending ? "Creating..." : "Create Share Link"}
                </Button>

                {createShareMutation.isError && (
                  <p className="text-sm text-red-600">
                    {createShareMutation.error instanceof Error
                      ? createShareMutation.error.message
                      : "Failed to create share link"}
                  </p>
                )}
              </>
            )}
          </div>

          {/* Active Shares Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">Active Share Links</h3>
            {sharesLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : activeShares.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No active share links. Create one above to get started.
              </p>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Link</TableHead>
                      <TableHead>Expires</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeShares.map((share) => (
                      <TableRow key={share.uuid}>
                        <TableCell>
                          <div className="flex items-center gap-2 max-w-xs">
                            <code className="text-xs bg-muted px-2 py-1 rounded truncate flex-1">
                              {share.share_url}
                            </code>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleCopyLink(share.share_url)}
                            >
                              {copiedShareUrl === share.share_url ? (
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                              ) : (
                                <Copy className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{formatExpiryDate(share.expires_at)}</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {share.is_password_protected && (
                              <Badge variant="secondary" className="text-xs">
                                Password Protected
                              </Badge>
                            )}
                            {share.is_revoked && (
                              <Badge variant="destructive" className="text-xs">
                                Revoked
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-600 hover:text-red-700"
                            onClick={() => handleRevokeShare(share.uuid)}
                            disabled={revokingShareUuid === share.uuid || revokeShareMutation.isPending}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
